import { useState, useRef, useEffect } from 'react';
import './Admin.css';

/**
 * Drupal-style taxonomy: type to search existing terms, add by Enter or click.
 * Selected terms shown as removable chips. "Create [name]" for new terms.
 */
export default function AdminTechnologyTaxonomy({
  terms = [],
  selectedIds = [],
  newTermsString = '',
  onChange,
  placeholder = 'Search or type a term, press Enter to add',
  disabled,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const newTermsList = newTermsString
    ? newTermsString.split(',').map((s) => s.trim()).filter(Boolean)
    : [];
  const selectedNames = new Set([
    ...selectedIds.map((id) => terms.find((t) => t.id === id)?.name).filter(Boolean),
    ...newTermsList,
  ]);

  const queryTrimmed = query.trim();
  const queryLower = queryTrimmed.toLowerCase();
  const matchingTerms = queryLower
    ? terms.filter(
        (t) =>
          t.name.toLowerCase().includes(queryLower) && !selectedIds.includes(t.id)
      )
    : terms.filter((t) => !selectedIds.includes(t.id));

  const exactMatch = queryLower && terms.some((t) => t.name.toLowerCase() === queryLower);
  const canCreateNew = queryTrimmed && !exactMatch && !selectedNames.has(queryTrimmed);

  const options = [
    ...matchingTerms.slice(0, 15).map((t) => ({ type: 'existing', id: t.id, name: t.name })),
    ...(canCreateNew ? [{ type: 'new', name: queryTrimmed }] : []),
  ];

  const openDropdown = () => setOpen(true);
  const closeDropdown = () => {
    setOpen(false);
    setHighlightIndex(0);
  };

  useEffect(() => {
    if (!query.trim() && options.length === 0) closeDropdown();
  }, [query, options.length]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [options.length]);

  const addExisting = (id) => {
    if (selectedIds.includes(id)) return;
    onChange({ ids: [...selectedIds, id], new: newTermsString });
    setQuery('');
    inputRef.current?.focus();
  };

  const addNew = (name) => {
    const trimmed = name.trim();
    if (!trimmed || selectedNames.has(trimmed)) return;
    const next = newTermsString ? `${newTermsString}, ${trimmed}` : trimmed;
    onChange({ ids: selectedIds, new: next });
    setQuery('');
    inputRef.current?.focus();
  };

  const removeById = (id) => {
    onChange({ ids: selectedIds.filter((i) => i !== id), new: newTermsString });
  };

  const removeNewTerm = (name) => {
    const list = newTermsList.filter((n) => n !== name);
    onChange({ ids: selectedIds, new: list.join(', ') });
  };

  const handleKeyDown = (e) => {
    if (!open || options.length === 0) {
      if (e.key === 'Enter' && queryTrimmed && canCreateNew) {
        e.preventDefault();
        addNew(queryTrimmed);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % options.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i - 1 + options.length) % options.length);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[highlightIndex];
      if (opt) {
        if (opt.type === 'existing') addExisting(opt.id);
        else addNew(opt.name);
      } else if (queryTrimmed && canCreateNew) addNew(queryTrimmed);
      return;
    }
    if (e.key === 'Escape') {
      closeDropdown();
      inputRef.current?.blur();
    }
  };

  const getNameById = (id) => terms.find((t) => t.id === id)?.name || '';

  return (
    <div className="admin-tech-taxonomy">
      <div className="tech-selected-chips">
        {selectedIds.map((id) => {
          const name = getNameById(id);
          if (!name) return null;
          return (
            <span key={`id-${id}`} className="tech-selected-chip">
              {name}
              <button
                type="button"
                className="tech-chip-remove"
                onClick={() => removeById(id)}
                aria-label={`Remove ${name}`}
              >
                ×
              </button>
            </span>
          );
        })}
        {newTermsList.map((name) => (
          <span key={`new-${name}`} className="tech-selected-chip tech-selected-chip--new">
            {name}
            <button
              type="button"
              className="tech-chip-remove"
              onClick={() => removeNewTerm(name)}
              aria-label={`Remove ${name}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="tech-autocomplete-row">
        <input
          ref={inputRef}
          type="text"
          className="tech-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => openDropdown()}
          onBlur={() => setTimeout(closeDropdown, 200)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={open && options.length > 0}
          aria-controls="tech-autocomplete-list"
          id="tech-taxonomy-input"
        />
        {open && options.length > 0 && (
          <div
            ref={listRef}
            id="tech-autocomplete-list"
            className="tech-autocomplete-dropdown"
            role="listbox"
          >
            {options.map((opt, i) => (
              <div
                key={opt.type === 'existing' ? opt.id : `new-${opt.name}`}
                role="option"
                aria-selected={i === highlightIndex}
                className={`tech-ac-item ${opt.type === 'new' ? 'tech-ac-item-new' : ''} ${i === highlightIndex ? 'tech-ac-item--highlight' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (opt.type === 'existing') addExisting(opt.id);
                  else addNew(opt.name);
                }}
                onMouseEnter={() => setHighlightIndex(i)}
              >
                {opt.type === 'new' ? `Create "${opt.name}"` : opt.name}
              </div>
            ))}
          </div>
        )}
      </div>
      <span className="tech-autocomplete-hint">
        Type to search existing terms or enter a new one. Press <kbd>Enter</kbd> to add.
      </span>
    </div>
  );
}
